import { Center } from '@euler/components/Center';
import { MaybeText } from '@euler/components/MaybeText';
import * as React from 'react';
import { LegacyRef, ReactNode } from 'react';
import {
  ActivityIndicator,
  Platform,
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  TextInputProps,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import PlatformColor from './PlatformColor';
import useDialogTheme, { StyleBuilder } from './useTheme';

export interface DialogInputProps extends TextInputProps {
  label?: ReactNode;
  wrapperStyle?: StyleProp<ViewStyle>;
  textInputRef?: LegacyRef<TextInput>;
  unstableLabelStyle?: StyleProp<TextStyle>;
  loading?: boolean;
  errorMsg?: ReactNode;
  errorStyle?: StyleProp<TextStyle>;
  prefix?: ReactNode;
  suffix?: ReactNode;
}

const DialogInput: React.FC<DialogInputProps> = props => {
  const {
    label,
    style,
    wrapperStyle,
    textInputRef,
    multiline,
    numberOfLines,
    unstableLabelStyle,
    loading,
    editable,
    prefix,
    suffix,
    errorMsg,
    errorStyle,
    ...nodeProps
  } = props;
  const lines = (multiline && numberOfLines) || 1;
  const height =
    18 + Platform.select({ ios: 14, android: 22, default: 0 }) * lines;
  const { styles, isDark } = useDialogTheme(buildStyles);
  return (
    <>
      <View
        style={[
          styles.textInputWrapper,
          errorMsg ? styles.textInputWrapperWithError : undefined,
          wrapperStyle,
        ]}
      >
        {label && (
          <Text style={[styles.label, unstableLabelStyle]}>{label}</Text>
        )}
        {prefix}
        <TextInput
          ref={textInputRef}
          placeholderTextColor={
            Platform.OS === 'ios'
              ? PlatformColor('placeholderText')
              : PlatformColor(
                  `@android:color/${
                    isDark ? 'hint_foreground_dark' : 'hint_foreground_light'
                  }`,
                )
          }
          underlineColorAndroid={PlatformColor(
            `@android:color/${
              isDark ? 'hint_foreground_dark' : 'hint_foreground_light'
            }`,
          )}
          style={[
            styles.textInput,
            style,
            { height },
            loading ? { paddingRight: 34 } : undefined,
            errorMsg ? styles.textInputWithError : undefined,
          ]}
          multiline={multiline}
          numberOfLines={numberOfLines}
          editable={editable ?? !loading}
          {...nodeProps}
        />
        {suffix}
        {loading && (
          <Center
            css={`
              position: absolute;
              right: 0;
              top: 0;
              bottom: 0;
              width: 34px;
            `}
          >
            <ActivityIndicator size="small" />
          </Center>
        )}
      </View>
      {errorMsg ? (
        <MaybeText text={errorMsg} style={[styles.errorMsg, errorStyle]} />
      ) : null}
    </>
  );
};

DialogInput.displayName = 'DialogInput';

const buildStyles: StyleBuilder = isDark =>
  StyleSheet.create({
    textInputWrapper: Platform.select({
      default: {
        flexDirection: 'row',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        backgroundColor: PlatformColor('systemGray5'),
        borderWidth: StyleSheet.hairlineWidth,
        borderRadius: 6,
        borderColor: PlatformColor('separator'),
        marginHorizontal: 20,
        marginBottom: 20,
        paddingHorizontal: 8,
        overflow: 'hidden',
      },
      android: {
        flexDirection: 'row',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        marginHorizontal: 10,
        marginBottom: 20,
        overflow: 'hidden',
      },
    }),
    textInputWrapperWithError: Platform.select({
      default: {
        borderColor: PlatformColor('systemRed'),
      },
      android: {
        borderColor: PlatformColor('@android:color/system_error_600'),
      },
    }),
    label: Platform.select({
      default: {
        position: 'absolute',
        left: 0,
        top: 0,
        right: 0,
        color: PlatformColor('label'),
      },
      android: {
        position: 'absolute',
        left: 0,
        top: 0,
        right: 0,
        color: PlatformColor(
          `@android:color/${
            isDark ? 'primary_text_dark' : 'primary_text_light'
          }`,
        ),
        fontSize: 14,
      },
    }),
    textInput: Platform.select({
      default: {
        flex: 1,
        color: PlatformColor('label'),
      },
      android: {
        flex: 1,
        color: PlatformColor(
          `@android:color/${
            isDark ? 'primary_text_dark' : 'primary_text_light'
          }`,
        ),
        marginLeft: -4,
        paddingLeft: 4,
      },
      web: {
        flex: 1,
        color: PlatformColor('label'),
        minHeight: 40,
        minWidth: 1,
      },
    }),
    textInputWithError: Platform.select({
      default: {
        color: PlatformColor('systemRed'),
      },
      android: {
        color: PlatformColor('@android:color/system_error_600'),
      },
    }),
    errorMsg: Platform.select({
      default: {
        color: PlatformColor('systemRed'),
        fontSize: 14,
        marginHorizontal: 20,
        marginTop: -15,
        marginBottom: 20,
      },
      android: {
        color: PlatformColor('@android:color/system_error_600'),
        fontSize: 14,
        marginTop: -15,
        marginHorizontal: 10,
        marginBottom: 20,
      },
    }),
  });

export default DialogInput;
